import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Check, FileText } from 'lucide-react';
import { Button, Progress, StatusBadge } from '@/design/components';
import { ModuleGate } from '@/access/Gate';
import { formatDate } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { isLate, useCompleteStep, useMyOnboarding, type OnboardingStep } from './api';

/**
 * הקליטה שלי, במסך הבית (PRD 5.11).
 *
 * הכרטיס נעלם מעצמו כשכל השלבים הושלמו: קליטה היא תקופה, לא תכונה
 * קבועה במסך.
 */
export function OnboardingCard() {
  return (
    <ModuleGate module="learning" fallback={null}>
      <OnboardingCardInner />
    </ModuleGate>
  );
}

function OnboardingCardInner() {
  const onboarding = useMyOnboarding();
  const complete = useCompleteStep();
  const [error, setError] = useState<string | null>(null);

  const data = onboarding.data;
  if (!data || data.steps.length === 0) return null;

  const done = data.steps.filter((step) => step.completed_at).length;
  if (done === data.steps.length) return null;

  const next = data.steps.filter((step) => !step.completed_at);

  return (
    <section className="grid gap-3 rounded-lg bg-raised p-4 shadow-card">
      <div className="grid gap-1">
        <h2 className="m-0 font-display text-display-m font-semibold">הקליטה שלך</h2>
        <p className="m-0 text-body-sm text-muted">
          התחלת ב {formatDate(data.start_date)}. אלה הדברים שכדאי לסגור.
        </p>
      </div>

      <Progress
        value={done}
        max={data.steps.length}
        label={`${done} מתוך ${data.steps.length} שלבים`}
      />

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <ul className="m-0 grid list-none gap-2 p-0">
        {next.slice(0, 4).map((step) => (
          <li key={step.step_id}>
            <Step
              step={step}
              busy={complete.isPending}
              onDone={() => {
                setError(null);
                complete
                  .mutateAsync(step.step_id)
                  .catch((caught) => setError(errorMessage(caught)));
              }}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function Step({
  step,
  busy,
  onDone,
}: {
  step: OnboardingStep;
  busy: boolean;
  onDone: () => void;
}) {
  const late = isLate(step);

  return (
    <div className="grid gap-2 rounded-md border border-line p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="text-body font-medium text-ink">{step.title}</span>
        {late ? (
          <StatusBadge tone="bad">באיחור</StatusBadge>
        ) : (
          <span className="text-label text-muted">עד {formatDate(step.due_date)}</span>
        )}
      </div>

      {step.description && (
        <p className="m-0 text-body-sm text-muted">{step.description}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {/* שלב שתלוי בלומדה או בנוהל מוביל לשם, כי שם הוא באמת נסגר */}
        {step.course_id && (
          <Link
            to={`/learning/${step.course_id}`}
            className="inline-flex items-center gap-1 text-body-sm font-medium text-ink"
          >
            <BookOpen size={16} strokeWidth={1.75} aria-hidden="true" />
            ללומדה
          </Link>
        )}
        {step.document_id && (
          <Link
            to={`/documents/${step.document_id}`}
            className="inline-flex items-center gap-1 text-body-sm font-medium text-ink"
          >
            <FileText size={16} strokeWidth={1.75} aria-hidden="true" />
            לנוהל
          </Link>
        )}

        <Button variant="secondary" disabled={busy} onClick={onDone}>
          <Check size={18} strokeWidth={1.75} aria-hidden="true" />
          סיימתי
        </Button>
      </div>
    </div>
  );
}
